// JOBE-0098.3: open palette panel. Composes the input row, parser
// receipt, sections, and footer in document order. Owns highlight
// index navigation across the active section.

import { useEffect, useMemo, useRef, useState } from 'react';
import { InputRow } from './InputRow.jsx';
import { ParserReceipt } from './ParserReceipt.jsx';
import { Footer } from './Footer.jsx';
import { EmptyState } from './sections/EmptyState.jsx';
import { AutocompleteSection, optionsForPredicate } from './sections/AutocompleteSection.jsx';
import { MatchesSection } from './sections/MatchesSection.jsx';
import { PlainTextFallback, promotionsFor } from './sections/PlainTextFallback.jsx';
import { pickState } from './pickState.js';
import { parse } from '../../lib/omni-parser';

function chipFromPredicate(p) {
  return {
    kind: 'predicate',
    friendlyToken: p.friendlyToken,
    wirePredicate: p.rawPredicate,
    values: p.values,
  };
}

// applyAutocomplete: replace the trailing partial-value token in the
// input string with the picked option's wire value.
function applyAutocomplete(input, cursorPredicate, option) {
  if (!cursorPredicate || !option) return input;
  const span = cursorPredicate.span || {};
  const start = typeof span.start === 'number' ? span.start : 0;
  const end = typeof span.end === 'number' ? span.end : input.length;
  const prefix = input.slice(0, start);
  const suffix = input.slice(end);
  const op = cursorPredicate.op || ':';
  return `${prefix}${cursorPredicate.friendlyToken}${op}${option.value}${suffix}`;
}

export function PalettePanel({
  placeholderVariant = 'library',
  sources,
  recents,
  onPushRecent,
  onApplyFilters,
  onSaveView,
  onNavigate,
  onSelectRow,
  onSearch,
  onClose,
}) {
  const [input, setInput] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const parsed = useMemo(() => parse(input), [input]);
  const stateId = pickState(parsed, input);

  useEffect(() => {
    setHighlightIndex(0);
  }, [stateId]);

  const cursorPredicate = parsed.predicates && parsed.predicates.length > 0
    ? parsed.predicates[parsed.predicates.length - 1]
    : null;
  const partial = cursorPredicate ? (cursorPredicate.values || []).join(',') : '';

  // Visible-option count for the active section — used to clamp
  // ArrowDown so the highlight ring can't walk off the end (PR #696
  // LOW). Matches sections' rows are async, so 'mixed' and 'plain'
  // fall back to 0 here (clamp is permissive — ArrowDown stays a
  // no-op when we don't know the count yet, which is fine).
  let activeOptionCount = 0;
  if (stateId === 'predicate') {
    activeOptionCount = optionsForPredicate(
      cursorPredicate?.friendlyToken,
      partial,
      sources || {},
    ).length;
  } else if (stateId === 'empty') {
    activeOptionCount = 3 + (recents?.length || 0);
  } else if (stateId === 'plain') {
    const frag = parsed?.fuzzyFragment || '';
    const hasUnrec = (parsed?.unrecognized || []).length > 0;
    activeOptionCount = hasUnrec ? 0 : promotionsFor(frag).length;
  }

  const acceptAutocomplete = () => {
    if (stateId !== 'predicate') return;
    const opts = optionsForPredicate(cursorPredicate.friendlyToken, partial, sources || {});
    const picked = opts[highlightIndex] || opts[0];
    if (!picked) return;
    setInput(applyAutocomplete(input, cursorPredicate, picked));
  };

  // runAsTyped: bare ↩. Per spec §Keyboard chord table, this runs the
  // box as-typed and pushes to recents but does NOT mutate host filter
  // state — only ⇧↩ applies filters and closes. The refresh of the
  // Matches section happens via MatchesSection's onSearch effect; we
  // don't re-issue it here.
  const runAsTyped = () => {
    if (!input.trim()) return;
    const chips = (parsed.predicates || []).map(chipFromPredicate);
    if (onPushRecent) {
      onPushRecent({ query: input, chips });
    }
  };

  const applyFilters = () => {
    const chips = (parsed.predicates || []).map(chipFromPredicate);
    if (chips.length === 0) return;
    if (onPushRecent) {
      onPushRecent({ query: input, chips });
    }
    if (onApplyFilters) onApplyFilters(chips);
    if (onClose) onClose();
  };

  const saveView = () => {
    if (stateId !== 'mixed') return;
    const chips = (parsed.predicates || []).map(chipFromPredicate);
    // name is null in v1 — JOBE-0044.9 will wire a "Name this view?"
    // prompt and pass through a user-supplied name. Empty string would
    // ambiguate with "named-but-blank"; null is "no name yet."
    if (onSaveView) onSaveView(null, chips);
  };

  const pickRowFromMatches = (row) => {
    const urn = row.urn || row.id;
    if (!urn) return;
    if (placeholderVariant === 'entity-scoped') {
      if (onNavigate) onNavigate(urn);
    } else if (onSelectRow) {
      onSelectRow(urn);
    }
    if (onClose) onClose();
  };

  const pickPromotion = (promo) => {
    const next = `${promo.friendlyToken}${promo.op}${promo.values.join(',')}`;
    setInput(next);
  };

  const pickRecent = (recent) => {
    if (recent && typeof recent.query === 'string') {
      setInput(recent.query);
    }
  };

  const pickExample = (example) => {
    setInput(example);
    // Refocus the input so the user can keep typing after a click-pick
    // (NIT from PR #696 review).
    if (inputRef.current) inputRef.current.focus();
  };

  const onKeyDown = (e) => {
    const k = e.key;
    if (k === 'Escape') {
      e.preventDefault();
      if (onClose) onClose();
      return;
    }
    // ⌘1/⌘2/⌘3 jump to recents 1..3 from state 2 (empty input). The
    // EmptyState renders the hint pills so the chord must work or the
    // hint lies. We accept both meta and ctrl (host platform varies).
    if (stateId === 'empty' && (e.metaKey || e.ctrlKey) && /^[123]$/.test(k)) {
      const idx = Number(k) - 1;
      const recent = (recents || [])[idx];
      if (recent) {
        e.preventDefault();
        pickRecent(recent);
      }
      return;
    }
    if (k === 'Tab') {
      if (stateId === 'predicate') {
        e.preventDefault();
        acceptAutocomplete();
      }
      return;
    }
    if (k === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => {
        const cap = activeOptionCount;
        if (cap <= 0) return 0;
        return Math.min(i + 1, cap - 1);
      });
      return;
    }
    if (k === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (k === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        applyFilters();
        return;
      }
      if (e.metaKey || e.ctrlKey) {
        saveView();
        return;
      }
      runAsTyped();
      return;
    }
  };

  let body = null;
  if (stateId === 'empty') {
    body = (
      <EmptyState
        recents={recents}
        onPickRecent={pickRecent}
        onPickExample={pickExample}
      />
    );
  } else if (stateId === 'predicate') {
    body = (
      <AutocompleteSection
        cursorPredicate={cursorPredicate}
        partial={partial}
        sources={sources || {}}
        highlightIndex={highlightIndex}
        onHighlight={setHighlightIndex}
        onPick={(opt) => {
          setInput(applyAutocomplete(input, cursorPredicate, opt));
        }}
      />
    );
  } else if (stateId === 'mixed') {
    body = (
      <MatchesSection
        parsed={parsed}
        onSearch={onSearch}
        highlightIndex={highlightIndex}
        onHighlight={setHighlightIndex}
        onPick={pickRowFromMatches}
      />
    );
  } else {
    body = (
      <PlainTextFallback
        parsed={parsed}
        onSearch={onSearch}
        highlightIndex={highlightIndex}
        onHighlight={setHighlightIndex}
        onPick={pickRowFromMatches}
        onPickPromotion={pickPromotion}
      />
    );
  }

  const hasPredicates = parsed.predicates && parsed.predicates.length > 0;

  return (
    <div className="omni-panel" role="dialog" aria-label="Find palette" data-state={stateId}>
      <InputRow
        ref={inputRef}
        value={input}
        onChange={setInput}
        onKeyDown={onKeyDown}
      />
      <ParserReceipt parsed={parsed} />
      <div className="omni-body">{body}</div>
      <Footer stateId={stateId} hasPredicates={hasPredicates} />
    </div>
  );
}
